import { field } from "@platform/ui/styles";
import { OTPInput, OTPInputContext } from "input-otp";
import * as React from "react";
import { twMerge } from "tailwind-merge";
import { tv, VariantProps } from "tailwind-variants";

export { REGEXP_ONLY_CHARS, REGEXP_ONLY_DIGITS } from "input-otp";

type InputOTPProps = React.ComponentPropsWithoutRef<typeof OTPInput> & {
  className?: string;
};

export const InputOTP = React.forwardRef<React.ComponentRef<typeof OTPInput>, InputOTPProps>(
  ({ className, ...rest }, ref) => {
    return (
      <OTPInput
        ref={ref}
        autoComplete="off"
        data-1p-ignore
        data-lpignore="true"
        data-form-type="other"
        pushPasswordManagerStrategy="none"
        containerClassName={twMerge("@container/otp", "flex flex-nowrap gap-2 has-[:disabled]:opacity-50", className)}
        {...rest}
      />
    );
  },
);

InputOTP.displayName = "InputOTP";

interface InputOTPInputProps extends React.ComponentPropsWithoutRef<"div"> {
  index: number;
  size?: InputOTPInputVariants["size"];
}

export const InputOTPInput = React.forwardRef<HTMLDivElement, InputOTPInputProps>(
  ({ index, size, className, ...rest }, ref) => {
    const inputOTPContext = React.useContext(OTPInputContext);
    const slot = inputOTPContext.slots[index];

    if (!slot) {
      return null;
    }

    const { char, hasFakeCaret, isActive } = slot;

    return (
      <div
        ref={ref}
        className={inputOTPInputVariants({
          size,
          isActive,
          className,
        })}
        {...rest}
      >
        {char}
        {hasFakeCaret && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="animate-caret-blink h-4 w-px bg-current" />
          </div>
        )}
      </div>
    );
  },
);
InputOTPInput.displayName = "InputOTPInput";

const inputOTPInputVariants = tv({
  extend: field,
  base: [
    // Base Styles
    "relative flex items-center justify-center overflow-clip rounded-md text-center",
    "bg-(--field-bg) border border-(--field-border)",
    "transition-all inset-shadow-sm",
    "placeholder:text-(--field-text-placeholder)",
    "read-only:text-ds-text-tertiary",

    "text-(--field-text) outline-none",
  ],
  variants: {
    size: {
      default: "h-10 w-9 text-sm",
      lg: "h-12 w-11 md:h-16 md:w-15 text-xl",
      fluid: "aspect-[13/16] h-auto w-full text-[max(0.875rem,5cqw)]",
    },
    isActive: {
      true: [
        "bg-(--field-bg-focus)",
        "border-(--field-border-focus)",
        "ring-2 ring-(--field-ring)",
        "inset-shadow-none",
        "z-10",
      ],
      false: [],
    },
  },
  defaultVariants: {
    size: "default",
    isActive: false,
  },
});

type InputOTPInputVariants = VariantProps<typeof inputOTPInputVariants>;
